import React, {useContext, useState} from 'react';
import {makeStyles} from '@material-ui/core/styles';
import {Button, Container, FormHelperText, Slider, TextField, Typography} from '@material-ui/core';
import {FontAwesomeIcon} from '@fortawesome/react-fontawesome';
import {faSearch} from '@fortawesome/free-solid-svg-icons';
import FilterPanel from './FilterPanel';
import FilterContext from '../FilterContext';

const DEFAULT_RADIUS_METERS = 500;
const MIN_RADIUS_METERS = 100;
const MAX_RADIUS_METERS = 5000;

const useStyles = makeStyles((theme) => ({
    container: {
        textAlign: 'left',
        padding: '0.5rem 1rem'
    },
    searchInput: {
        width: '100%'
    },
    searchButton: {
        marginTop: theme.spacing(1)
    },
    sliderRow: {
        marginTop: theme.spacing(3)
    },
    clearButton: {
        marginTop: theme.spacing(2)
    },
    status: {
        marginTop: theme.spacing(1),
        minHeight: '1.2em'
    }
}));

const WhereFilters = (props) => {
    const classes = useStyles();
    const [context, setContext] = useContext(FilterContext);

    const [address, setAddress] = useState('');
    const [status, setStatus] = useState('');
    const [searching, setSearching] = useState(false);

    const currentRadius =
        context.filters.region && context.filters.region['@type'] === 'circle'
            ? context.filters.region.radius
            : DEFAULT_RADIUS_METERS;

    const handleAddressChange = (e) => setAddress(e.target.value);

    const handleSearch = async () => {
        const query = address.trim();
        if (!query) {
            setStatus('Enter an address to search.');
            return;
        }
        setSearching(true);
        setStatus('Searching...');
        try {
            const url =
                'https://nominatim.openstreetmap.org/search?format=json&limit=1&q=' +
                encodeURIComponent(query);
            const res = await fetch(url, {
                headers: {Accept: 'application/json'}
            });
            const data = await res.json();
            if (!data || data.length === 0) {
                setStatus('No location found for that address.');
                setSearching(false);
                return;
            }
            const lat = parseFloat(data[0].lat);
            const lon = parseFloat(data[0].lon);
            const existing = context.filters.region;
            const radius =
                existing && existing['@type'] === 'circle'
                    ? existing.radius
                    : DEFAULT_RADIUS_METERS;
            setContext({
                ...context,
                filters: {
                    ...context.filters,
                    region: {
                        '@type': 'circle',
                        lat,
                        lon,
                        radius
                    }
                },
                searchLocation: {lat, lon, label: data[0].display_name}
            });
            setStatus(data[0].display_name);
        } catch (err) {
            setStatus('Address lookup failed. Please try again.');
        } finally {
            setSearching(false);
        }
    };

    const handleKeyDown = (e) => {
        if (e.key === 'Enter') {
            e.preventDefault();
            handleSearch();
        }
    };

    const handleRadiusChange = (event, newValue) => {
        if (!context.searchLocation) return;
        setContext({
            ...context,
            filters: {
                ...context.filters,
                region: {
                    '@type': 'circle',
                    lat: context.searchLocation.lat,
                    lon: context.searchLocation.lon,
                    radius: newValue
                }
            }
        });
    };

    const handleClear = () => {
        const nextFilters = {...context.filters};
        delete nextFilters.region;
        setContext({
            ...context,
            filters: nextFilters,
            searchLocation: undefined
        });
        setAddress('');
        setStatus('');
    };

    const hasSearch = Boolean(context.searchLocation);

    return (
        <FilterPanel sidebarWidth={props.sidebarWidth} selected={props.selected} close={props.close}>
            <Container className={classes.container}>
                <h3>Search</h3>
                <TextField
                    className={classes.searchInput}
                    label="Address"
                    variant="outlined"
                    value={address}
                    onChange={handleAddressChange}
                    onKeyDown={handleKeyDown}
                    disabled={searching}
                    fullWidth
                    multiline
                    minRows={2}
                />
                <Button
                    className={classes.searchButton}
                    variant="contained"
                    color="primary"
                    onClick={handleSearch}
                    disabled={searching}
                    startIcon={<FontAwesomeIcon icon={faSearch}/>}
                    fullWidth
                >
                    Search
                </Button>
                <FormHelperText className={classes.status}>{status}</FormHelperText>

                <div className={classes.sliderRow}>
                    <Typography gutterBottom>
                        Radius: {currentRadius} m
                    </Typography>
                    <Slider
                        value={currentRadius}
                        min={MIN_RADIUS_METERS}
                        max={MAX_RADIUS_METERS}
                        step={50}
                        valueLabelDisplay="auto"
                        onChange={handleRadiusChange}
                        disabled={!hasSearch}
                    />
                </div>

                {hasSearch && (
                    <Button
                        className={classes.clearButton}
                        variant="outlined"
                        onClick={handleClear}
                        fullWidth
                    >
                        Clear search
                    </Button>
                )}
            </Container>
        </FilterPanel>
    );
};

export default WhereFilters;